import { Copy, ImageDown, Printer, RotateCcw, Volume2, VolumeX, Maximize2, Minimize2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { copyText } from "@/lib/random-engine";
import { toast } from "sonner";
import {
  sfxBeep,
  sfxClick,
  sfxDiceLand,
  sfxDiceRattle,
  sfxFanfare,
  sfxTick,
  sfxWhoosh,
  tone,
} from "@/lib/sound";

export function Panel({
  title,
  children,
  className = "",
  action,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <section className={`surface-card print-clean p-5 ${className}`}>
      {title ? (
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {title}
          </h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function ListInput({
  value,
  onChange,
  label = "Names",
  placeholder = "One name per line",
  count,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  placeholder?: string;
  count: number;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm font-medium" htmlFor="tool-list">
          {label}
        </label>
        <span className="text-xs text-muted-foreground">{count} entries</span>
      </div>
      <Textarea
        id="tool-list"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={10}
        className="resize-y font-mono text-sm"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => onChange("")}>
          Clear list
        </Button>
        <label className="inline-flex cursor-pointer items-center rounded-md border border-input px-3 text-xs font-medium leading-8 transition-colors hover:bg-secondary">
          Import TXT/CSV
          <input
            type="file"
            accept=".txt,.csv,text/plain,text/csv"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const text = await file.text();
              onChange(text);
              e.target.value = "";
            }}
          />
        </label>
      </div>
    </div>
  );
}

/** Shared style for control chips that sit on gradient/dark stages as well as light cards. */
export const controlChip =
  "border-white/25 bg-white/90 text-slate-900 shadow-sm backdrop-blur-sm hover:bg-white hover:text-slate-900 dark:border-white/15 dark:bg-white/15 dark:text-white dark:hover:bg-white/25";

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={controlChip}
      onClick={async () => {
        await copyText(text);
        toast.success("Copied to clipboard");
      }}
    >
      <Copy className="size-4" /> {label}
    </Button>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function downloadBrandedPng(title: string, content: string, filename: string) {
  const width = 1080;
  const lines = content.split("\n").flatMap((line) => {
    if (!line.trim()) return [""];
    const words = line.split(" ");
    const wrapped: string[] = [];
    let current = "";
    for (const word of words) {
      const next = current ? `${current} ${word}` : word;
      if (next.length > 34 && current) {
        wrapped.push(current);
        current = word;
      } else {
        current = next;
      }
    }
    if (current) wrapped.push(current);
    return wrapped;
  });
  const titleLines = title.length > 27 ? [title.slice(0, 27), title.slice(27)] : [title];
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = Math.max(980, 690 + lines.length * 58);
  const context = canvas.getContext("2d");
  if (!context) return;

  context.fillStyle = "#eef2ff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  const headerGradient = context.createLinearGradient(0, 0, width, 300);
  headerGradient.addColorStop(0, "#4338ca");
  headerGradient.addColorStop(1, "#2563eb");
  context.fillStyle = headerGradient;
  context.fillRect(0, 0, width, 300);
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.roundRect(72, 72, 112, 112, 28);
  context.fill();
  context.fillStyle = "#4338ca";
  context.font = "800 64px Arial";
  context.fillText("P", 108, 148);
  context.fillStyle = "#ffffff";
  context.font = "800 40px Arial";
  context.fillText("Picamini.online", 220, 137);
  context.font = "600 22px Arial";
  context.globalAlpha = 0.88;
  context.fillText("Free classroom tools", 224, 176);
  context.globalAlpha = 1;
  context.fillStyle = "#ffffff";
  context.beginPath();
  context.roundRect(56, 250, width - 112, canvas.height - 330, 32);
  context.fill();
  context.fillStyle = "#64748b";
  context.font = "700 20px Arial";
  context.fillText("RESULT CARD", 96, 326);
  context.fillStyle = "#0f172a";
  context.font = "800 42px Arial";
  titleLines.forEach((line, index) => context.fillText(line, 96, 385 + index * 52));
  context.fillStyle = "#64748b";
  context.font = "500 20px Arial";
  context.fillText(`Generated ${new Date().toLocaleDateString()}`, 96, 495);
  context.fillStyle = "#1e293b";
  context.font = "600 32px Arial";
  const contentStart = titleLines.length > 1 ? 590 : 535;
  lines.forEach((line, index) => context.fillText(line, 96, contentStart + index * 58));
  context.fillStyle = "#64748b";
  context.font = "600 18px Arial";
  context.fillText("picamini.online  •  Free classroom tools", 96, canvas.height - 48);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (blob) downloadBlob(blob, filename);
}

export function BrandedExportActions({
  title,
  content,
  filename,
  printHeader = true,
  includePng = true,
}: {
  title: string;
  content: string;
  filename: string;
  printHeader?: boolean;
  includePng?: boolean;
}) {
  return (
    <>
      {printHeader ? (
        <div className="hidden print:block mb-8 border-b-2 border-indigo-600 pb-5 text-center">
          <p className="text-2xl font-black tracking-tight text-indigo-700">Picamini.online</p>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">Generated {new Date().toLocaleString()}</p>
          <p className="mt-5 whitespace-pre-wrap text-lg font-semibold leading-relaxed text-slate-900">{content}</p>
        </div>
      ) : null}
      <div className="no-print flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => window.print()}>
          <Printer className="size-3.5 mr-1.5" /> Print / PDF
        </Button>
        {includePng ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void downloadBrandedPng(title, content, filename)
                .then(() => toast.success("Downloaded branded PNG"))
                .catch(() => toast.error("PNG download is not supported in this browser"));
            }}
          >
            <ImageDown className="size-3.5 mr-1.5" /> Download PNG
          </Button>
        ) : null}
      </div>
    </>
  );
}

export function ResetButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm" className={controlChip} onClick={onClick}>
      <RotateCcw className="size-4" /> Reset
    </Button>
  );
}

export function SoundToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={controlChip}
      aria-label={on ? "Turn sound off" : "Turn sound on"}
      onClick={onToggle}
    >
      {on ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
      Sound
    </Button>
  );
}

export function FullscreenButton({
  targetRef,
}: {
  targetRef: React.RefObject<HTMLElement | null>;
}) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    const applyZoom = () => {
      if (document.fullscreenElement !== el) return;
      const natural = el.dataset["fsNaturalW"];
      const naturalH = el.dataset["fsNaturalH"];
      const w = Number(natural) || el.clientWidth;
      const h = Number(naturalH) || el.clientHeight;
      if (!w || !h) return;
      const zoom = Math.min(window.innerWidth / w, window.innerHeight / h, 2.6);
      el.style.setProperty("--fs-zoom", String(Math.max(1, Number(zoom.toFixed(3)))));
    };

    const onChange = () => {
      const isFs = document.fullscreenElement === el;
      setActive(isFs);
      el.classList.toggle("fs-stage", isFs);
      if (isFs) requestAnimationFrame(applyZoom);
      else {
        el.style.removeProperty("--fs-zoom");
        delete el.dataset["fsNaturalW"];
        delete el.dataset["fsNaturalH"];
      }
    };

    document.addEventListener("fullscreenchange", onChange);
    window.addEventListener("resize", applyZoom);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      window.removeEventListener("resize", applyZoom);
    };
  }, [targetRef]);

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={controlChip}
      aria-pressed={active}
      aria-label={active ? "Exit fullscreen" : "Enter fullscreen"}
      title={active ? "Exit fullscreen (Esc)" : "Present fullscreen"}
      onClick={() => {
        const el = targetRef.current;
        if (!el) return;
        if (document.fullscreenElement) {
          void document.exitFullscreen();
          return;
        }
        const rect = el.getBoundingClientRect();
        el.dataset["fsNaturalW"] = String(Math.round(rect.width));
        el.dataset["fsNaturalH"] = String(Math.round(rect.height));
        void el.requestFullscreen?.({ navigationUI: "hide" });
      }}
    >
      {active ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
      {active ? "Exit" : "Fullscreen"}
    </Button>
  );
}

export function HistoryPanel({
  items,
  onClear,
  title = "History",
}: {
  items: string[];
  onClear: () => void;
  title?: string;
}) {
  return (
    <Panel
      title={title}
      action={
        items.length ? (
          <div className="flex gap-1">
            <CopyButton text={items.join("\n")} label="" />
            <Button type="button" variant="ghost" size="sm" onClick={onClear}>
              Clear
            </Button>
          </div>
        ) : null
      }
    >
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing yet — results will appear here.</p>
      ) : (
        <ol className="max-h-64 space-y-1 overflow-auto text-sm">
          {items.map((item, i) => (
            <li
              key={`${item}-${i}`}
              className="flex gap-2 rounded-md px-2 py-1 odd:bg-secondary/60"
            >
              <span className="w-6 shrink-0 text-muted-foreground">{items.length - i}.</span>
              <span className="font-medium">{item}</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

export function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="text-sm font-medium text-destructive">
      {children}
    </p>
  );
}

/** Small WebAudio blip generator so no audio files are needed. */
export function useSound(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  useEffect(() => () => void ctxRef.current?.close(), []);
  return useCallback(
    (freq = 620, duration = 0.08, type: OscillatorType = "triangle") => {
      if (!enabled || typeof window === "undefined") return;
      try {
        ctxRef.current ??= new AudioContext();
        const ctx = ctxRef.current;
        void ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.08, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + duration);
      } catch {
        /* audio unavailable */
      }
    },
    [enabled],
  );
}

export function usePersistentState<T>(key: string, initial: T) {
  return usePersistentStateImpl(key, initial);
}

/** Named sound effects, gated by the tool's sound toggle. */
export function useSfx(enabled: boolean) {
  const ref = useRef(enabled);
  ref.current = enabled;
  const wrap = useCallback(
    <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (!ref.current) return;
        try {
          fn(...args);
        } catch {
          /* audio unavailable */
        }
      },
    [],
  );
  return useRef({
    tick: wrap(sfxTick),
    click: wrap(sfxClick),
    whoosh: wrap(sfxWhoosh),
    fanfare: wrap(sfxFanfare),
    beep: wrap(sfxBeep),
    tone: wrap(tone),
    diceRattle: wrap(sfxDiceRattle),
    diceLand: wrap(sfxDiceLand),
  }).current;
}

function usePersistentStateImpl<T>(key: string, initial: T) {
  const [state, setState] = useState<T>(initial);
  const [hydrated, setHydrated] = useState(false);
  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    setHydrated(false);
    try {
      const legacyKey = key.startsWith("pickamini:") ? key.replace("pickamini:", "eduzuma:") : null;
      const raw = localStorage.getItem(key) ?? (legacyKey ? localStorage.getItem(legacyKey) : null);
      if (raw != null) {
        setState(JSON.parse(raw) as T);
      } else {
        setState(initialRef.current);
      }
    } catch {
      setState(initialRef.current);
    }
    setHydrated(true);
  }, [key]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [key, state, hydrated]);

  return [state, setState] as const;
}
